'use client';

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useAppSelector } from '@/store/store';
import { useGetMyEntitlementsQuery } from '@/store/api/entitlementsApi';
import type {
  EntitlementDecision,
  ResolvedEntitlements,
  ResolvedPlanSummary,
} from '@/lib/types/entitlements.types';
import {
  assertEntitlementKeyParity,
  entitlementLabel,
} from '@/lib/utils/entitlement-keys';
import { FreePlanRestrictionModal } from './FreePlanRestrictionModal';

/**
 * The answer to "may they consume `quantity` more of this allowance?".
 *
 * Separate from a plain boolean because a caller that is about to be blocked
 * needs the numbers to say why — "50,000 of 50,000 records" is a usable
 * message and "not allowed" is not.
 */
export interface LimitCheck {
  /** Whether the gate should let this through. Honours `enforced`. */
  allowed: boolean;
  /** The ceiling, or null when unlimited / not a limit feature. */
  limit: number | null;
  /** Usage the service counted, or the value the caller supplied. */
  used: number;
  /** Headroom, or null when unlimited. */
  remaining: number | null;
  /**
   * False when the ceiling could not be measured — the service could not see
   * the count and the caller passed none. The allow is then based on no
   * evidence, and this is what stops that being read as headroom.
   */
  counted: boolean;
}

interface EntitlementGuardContextValue {
  /** The full resolved entitlements, or null while loading / on error. */
  entitlements: ResolvedEntitlements | null;
  /** The tier the user resolved through. Null when they are on none. */
  plan: ResolvedPlanSummary | null;
  isLoading: boolean;
  /**
   * Whether denials are in force at all. False means the whole system is an
   * honest dry run: decisions are real, nothing blocks. Useful for admin
   * screens that want to show what WOULD happen. True when the service says so
   * or when the dev override below is set.
   */
  isEnforced: boolean;
  /**
   * Whether a feature is allowed. Returns true when entitlements have not
   * loaded — a network failure must not lock the product.
   */
  isFeatureAllowed: (featureKey: string) => boolean;
  /** The full decision (reason, limit, used, source) for a feature. */
  getFeatureDecision: (featureKey: string) => EntitlementDecision | undefined;
  /** Whether this feature is denied AND the denial is enforced. */
  isFeatureDenied: (featureKey: string) => boolean;
  /**
   * The ceiling to CLAMP against on a limit feature. `null` means no cap, which
   * callers must handle distinctly from 0 — zero is a ceiling nobody can be
   * under.
   *
   * Null while denials are not in force, so a cap cannot quietly restrict the
   * UI ahead of enforcement. For the real number to DISPLAY, regardless of
   * enforcement, read `getFeatureDecision(key)?.limit`.
   */
  getLimit: (featureKey: string) => number | null;
  /**
   * Whether `quantity` more units fit under the ceiling.
   *
   * `used` overrides what the service counted, and is required for the
   * allowances the service cannot see itself (how many rows a list already
   * holds, how many saved searches exist). For a per-request cap — "50,000
   * records per export" — pass `used: 0` and the request size as `quantity`.
   */
  checkLimit: (
    featureKey: string,
    opts?: { quantity?: number; used?: number }
  ) => LimitCheck;
  /** Opens the upgrade modal for a denied feature. */
  openDenialModal: (featureKey: string, operation: string) => void;
  /**
   * Opens the upgrade modal for a limit that has been exceeded, with the
   * numbers filled in from a checkLimit result.
   */
  openLimitModal: (featureKey: string, operation: string, check: LimitCheck) => void;
  /** Re-read entitlements from the service, e.g. after a tier change. */
  refresh: () => void;
}

const EntitlementGuardContext = createContext<EntitlementGuardContextValue | undefined>(
  undefined
);

/**
 * How long a resolved bundle is trusted before it is re-read, in ms.
 *
 * Entitlements are not static the way the previous 24h cache assumed: an admin
 * raising a tier's ceiling, or granting one customer an exception, changes them
 * mid-session and the customer should not have to sign out to see it. Five
 * minutes is the same window the service's own caches use, and the request is
 * two queries upstream.
 */
const REVALIDATE_MS = 5 * 60 * 1000;

/**
 * Dev-only override that makes the client act on denials the service is
 * reporting but not yet enforcing.
 *
 * `ENTITLEMENTS_ENFORCED` is false upstream and will stay false until the whole
 * user base has been subscribed — but while it is false the resolver still
 * computes the TRUE answer, so every decision is real and only the switch is
 * off. This flips that switch locally, which is what makes the gates testable
 * per plan: subscribe a test account to Pro, set this, and the UI behaves
 * exactly as it will on the day enforcement goes on globally.
 *
 *   NEXT_PUBLIC_ENTITLEMENTS_FORCE_ENFORCE=true
 *
 * It invents nothing. It does not derive entitlements from a plan name, grant
 * anything, or fabricate a decision — it only stops discarding the denials the
 * service already returned. That distinction is the whole reason it is safe and
 * the fnx legacy bridge was not.
 *
 * Hard-disabled in production by the NODE_ENV check, so setting it in a prod
 * environment does nothing. Never gate real access on this.
 */
const FORCE_ENFORCE =
  process.env.NODE_ENV !== 'production' &&
  process.env.NEXT_PUBLIC_ENTITLEMENTS_FORCE_ENFORCE === 'true';

/** Whether a decision should be acted on, honouring the dev override. */
function isActedOn(decision: EntitlementDecision): boolean {
  return decision.enforced || FORCE_ENFORCE;
}

/**
 * Entitlement guard — sits BEFORE the credit guard in the access stack.
 *
 * This checks: "Is the user allowed to do this AT ALL?" (their tier, their
 * ceilings, any per-account exception). The credit guard checks: "Can they
 * afford it?" Two separate gates, and entitlements comes first.
 *
 * Mounted once inside the authenticated shell (client-layout.tsx).
 *
 * Every gate here is UI only. The authoritative refusal happens upstream —
 * the credit service asserts the feature key at debit time for anything that
 * costs credit — so a gate going wrong costs a confusing screen, never a
 * charge the customer was not entitled to.
 */
export function EntitlementGuardProvider({ children }: { children: React.ReactNode }) {
  const isAuthenticated = useAppSelector((state) => state.auth.isAuthenticated);

  const { data, isLoading: isEntitlementsLoading, refetch } =
    useGetMyEntitlementsQuery(undefined, {
      skip: !isAuthenticated,
      // A tier edit or a hand-set exception lands while the tab is open, so
      // coming back to it is the cheapest moment to notice.
      refetchOnFocus: true,
      refetchOnReconnect: true,
    });
  const entitlements = data ?? null;

  const [modalContext, setModalContext] = useState<{
    featureKey: string;
    decision: EntitlementDecision;
    operation: string;
  } | null>(null);

  const closeModal = useCallback(() => setModalContext(null), []);

  // Periodic revalidation on top of focus/reconnect, so a tab left open on the
  // search screen still picks up an upgrade without being touched.
  useEffect(() => {
    if (!isAuthenticated) return;
    const timer = setInterval(() => refetch(), REVALIDATE_MS);
    return () => clearInterval(timer);
  }, [isAuthenticated, refetch]);

  // Compare the key file against the live catalogue, once per bundle shape.
  // The failure it catches is otherwise completely silent: an unknown key
  // resolves "notCatalogued", which is ALLOWED, so a typo turns a gate into a
  // no-op with nothing logged anywhere.
  const parityCheckedRef = useRef(false);
  useEffect(() => {
    if (!entitlements || parityCheckedRef.current) return;
    parityCheckedRef.current = true;
    assertEntitlementKeyParity(Object.keys(entitlements.entitlements));
  }, [entitlements]);

  const getFeatureDecision = useCallback(
    (featureKey: string): EntitlementDecision | undefined =>
      entitlements?.entitlements[featureKey],
    [entitlements]
  );

  const isFeatureAllowed = useCallback(
    (featureKey: string): boolean => {
      // Fail open: not loaded, or a key the service does not know, must not
      // block. Denying on a failed fetch would take the whole product down
      // with the credit service.
      if (!entitlements) return true;
      return entitlements.entitlements[featureKey]?.allowed ?? true;
    },
    [entitlements]
  );

  const isFeatureDenied = useCallback(
    (featureKey: string): boolean => {
      const decision = getFeatureDecision(featureKey);
      if (!decision) return false;
      // Mirrors the service's shouldBlock(): a decision is only acted on when
      // denials are in force.
      return !decision.allowed && isActedOn(decision);
    },
    [getFeatureDecision]
  );

  const getLimit = useCallback(
    (featureKey: string): number | null => {
      const decision = getFeatureDecision(featureKey);
      if (!decision) return null;
      // Null - "no ceiling" - while denials are not in force, so a ceiling
      // read here cannot quietly restrict the UI ahead of enforcement. A cap
      // is as much a denial as a switch, and applying one while every other
      // gate is inert would make the rollout dishonest: the pager would drop
      // pages and the select dropdown would shrink on the day this shipped,
      // not on the day the switch was thrown.
      //
      // For DISPLAY - "8 of 10 saved searches used" - read
      // getFeatureDecision(key).limit instead, which is always the real number.
      if (!isActedOn(decision)) return null;
      // Denied outright - no plan, not in the plan, switched off - is a ceiling
      // of ZERO, not "no ceiling". Returning decision.limit here would hand back
      // null (a denial carries no limit) and read as unlimited, so the two
      // clamp-only gates would sail through for exactly the accounts that
      // should be most restricted. A free account has no config document at
      // all, so this is its normal state, not an edge case.
      if (!decision.allowed) return 0;
      return decision.limit ?? null;
    },
    [getFeatureDecision]
  );

  const checkLimit = useCallback(
    (
      featureKey: string,
      opts: { quantity?: number; used?: number } = {}
    ): LimitCheck => {
      const decision = getFeatureDecision(featureKey);
      const quantity = opts.quantity ?? 1;

      // Unknown key, or nothing loaded: unlimited and uncounted, so nothing
      // blocks and no caller mistakes that for measured headroom.
      if (!decision) {
        return { allowed: true, limit: null, used: 0, remaining: null, counted: false };
      }

      // A feature switched off outright is denied whatever its ceiling says,
      // and reporting that here means one call site handles both halves.
      if (!decision.allowed && decision.reason !== 'limitReached') {
        return {
          allowed: !isActedOn(decision),
          limit: decision.limit,
          used: decision.used ?? 0,
          remaining: decision.remaining,
          counted: decision.counted,
        };
      }

      // null is UNLIMITED, and deliberately not conflated with 0.
      if (decision.limit === null) {
        return { allowed: true, limit: null, used: opts.used ?? decision.used ?? 0, remaining: null, counted: true };
      }

      // A caller-supplied count wins: for the allowances the service cannot
      // see, its own `used` is 0 with counted:false, and for the ones it can,
      // a caller holding a fresher number should not be overruled by a staler.
      const used = opts.used ?? decision.used ?? 0;
      const counted = opts.used !== undefined ? true : decision.counted;
      const remaining = Math.max(0, decision.limit - used);
      const withinLimit = used + quantity <= decision.limit;

      return {
        // Same discipline as isFeatureDenied: a breach only blocks when
        // denials are in force.
        allowed: withinLimit || !isActedOn(decision),
        limit: decision.limit,
        used,
        remaining,
        counted,
      };
    },
    [getFeatureDecision]
  );

  const openDenialModal = useCallback(
    (featureKey: string, operation: string) => {
      const decision = getFeatureDecision(featureKey);
      if (decision && isFeatureDenied(featureKey)) {
        setModalContext({ featureKey, decision, operation });
      }
    },
    [getFeatureDecision, isFeatureDenied]
  );

  const openLimitModal = useCallback(
    (featureKey: string, operation: string, check: LimitCheck) => {
      const decision = getFeatureDecision(featureKey);
      if (!decision) return;
      // A ceiling gate refuses for two different reasons, and they need
      // different words. The allowance really being used up is `limitReached`;
      // but the same call site also refuses when the tier does not include the
      // feature at all, and telling that customer "Plan limit reached, 0 of 0
      // used" is nonsense - there is no limit they are at. So the decision's
      // own reason wins whenever it is not a limit breach.
      const isBreach = decision.allowed || decision.reason === 'limitReached';
      setModalContext({
        featureKey,
        operation,
        decision: isBreach
          ? {
              // The stored decision answers "one more?"; this one answers "this
              // many more?", so the numbers the caller measured are what shows.
              ...decision,
              allowed: false,
              reason: 'limitReached',
              used: check.used,
              limit: check.limit,
              remaining: check.remaining,
            }
          : decision,
      });
    },
    [getFeatureDecision]
  );

  const refresh = useCallback(() => {
    refetch();
  }, [refetch]);

  const value = useMemo<EntitlementGuardContextValue>(
    () => ({
      entitlements,
      plan: entitlements?.plan ?? null,
      isLoading: isEntitlementsLoading,
      isEnforced: (entitlements?.plan?.enforced ?? false) || FORCE_ENFORCE,
      isFeatureAllowed,
      getFeatureDecision,
      isFeatureDenied,
      getLimit,
      checkLimit,
      openDenialModal,
      openLimitModal,
      refresh,
    }),
    [
      entitlements,
      isEntitlementsLoading,
      isFeatureAllowed,
      getFeatureDecision,
      isFeatureDenied,
      getLimit,
      checkLimit,
      openDenialModal,
      openLimitModal,
      refresh,
    ]
  );

  return (
    <EntitlementGuardContext.Provider value={value}>
      {children}

      {modalContext ? (
        <FreePlanRestrictionModal
          open
          onClose={closeModal}
          entitlement={modalContext.decision}
          operation={modalContext.operation}
          featureLabel={entitlementLabel(modalContext.featureKey)}
        />
      ) : null}
    </EntitlementGuardContext.Provider>
  );
}

export function useEntitlementGuard(): EntitlementGuardContextValue {
  const context = useContext(EntitlementGuardContext);
  if (!context) {
    throw new Error('useEntitlementGuard must be used inside <EntitlementGuardProvider>');
  }
  return context;
}

export function useEntitlementCheck(featureKey: string) {
  const { isFeatureDenied, getFeatureDecision, isLoading } = useEntitlementGuard();

  return {
    isDenied: isFeatureDenied(featureKey),
    decision: getFeatureDecision(featureKey),
    isLoading,
  };
}

export default EntitlementGuardProvider;
